"use client";

import { useEffect, useRef, useState } from "react";

/* Live view Shoppub reconstruído na identidade do site, com os números
   do painel real. Os cards entram um a um (você "liga" o painel) e os
   dados seguem vivos: visitantes oscilam, o gráfico anda, o relógio conta. */

const SPARK_INICIAL = [22, 30, 26, 38, 31, 44, 36, 28, 41, 33, 47, 39, 30, 45, 37, 52, 43, 34, 48, 40, 55, 46, 38, 50];

const FONTES = [
  { nome: "direto", pct: 37, cor: "#c9f158" },
  { nome: "social", pct: 33, cor: "rgba(255,255,255,0.75)" },
  { nome: "orgânico", pct: 27, cor: "rgba(255,255,255,0.38)" },
  { nome: "outros", pct: 3, cor: "rgba(255,255,255,0.15)" },
];

const FUNIL = [
  { label: "Viu produto", n: 2466, pct: 100 },
  { label: "Add carrinho", n: 197, pct: 7.99 },
  { label: "Carrinho", n: 40, pct: 1.62 },
  { label: "Checkout", n: 109, pct: 4.42 },
  { label: "Finalizar", n: 43, pct: 1.74 },
];

const ESTADOS = [
  { uf: "São Paulo", n: 21 },
  { uf: "Minas Gerais", n: 7 },
  { uf: "Rio de Janeiro", n: 6 },
  { uf: "Rio Grande do Sul", n: 5 },
];

function Donut() {
  const r = 30, c = 2 * Math.PI * r;
  let acc = 0;
  return (
    <svg viewBox="0 0 80 80" className="h-20 w-20 -rotate-90">
      {FONTES.map((f) => {
        const seg = (f.pct / 100) * c;
        const el = (
          <circle
            key={f.nome}
            cx="40" cy="40" r={r}
            fill="none"
            stroke={f.cor}
            strokeWidth="9"
            strokeDasharray={`${seg} ${c - seg}`}
            strokeDashoffset={-acc}
          />
        );
        acc += seg;
        return el;
      })}
    </svg>
  );
}

export default function MockLiveview() {
  const raiz = useRef<HTMLDivElement>(null);
  const [on, setOn] = useState(false);
  const [visitantes, setVisitantes] = useState(67);
  const [spark, setSpark] = useState(SPARK_INICIAL);
  const [seg, setSeg] = useState(8);
  const [carrinhos, setCarrinhos] = useState(1);

  useEffect(() => {
    const el = raiz.current;
    if (!el) return;
    if (el.getBoundingClientRect().top < window.innerHeight * 0.9) {
      setOn(true);
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setOn(true);
          io.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!on) return;
    const t = setInterval(() => {
      setVisitantes((v) => Math.max(48, Math.min(96, v + Math.floor(Math.random() * 9) - 4)));
      setSpark((s) => [...s.slice(1), 18 + Math.floor(Math.random() * 38)]);
      setSeg((x) => (x % 9) + 1);
      if (Math.random() < 0.3) setCarrinhos(1 + Math.floor(Math.random() * 4));
    }, 2100);
    return () => clearInterval(t);
  }, [on]);

  // entrada sequencial dos cards
  const card = (i: number) =>
    `rounded-2xl border border-white/10 bg-ink p-4 transition-all duration-500 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] ${
      on ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
    }`;
  const delay = (i: number) => ({ transitionDelay: `${i * 170}ms` });

  const W = 220, H = 56;
  const pts = spark.map((v, i) => `${(i / (spark.length - 1)) * W},${H - (v / 60) * H}`).join(" ");

  return (
    <div ref={raiz} className="select-none rounded-card border border-white/10 bg-ink-soft p-4 sm:p-5">
      {/* cabeçalho */}
      <div
        className={`flex flex-wrap items-center justify-between gap-2 transition-all duration-500 ${
          on ? "opacity-100" : "opacity-0"
        }`}
      >
        <span className="flex items-center gap-2.5">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-lime opacity-60" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-lime" />
          </span>
          <span className="font-display text-base font-bold text-paper">Live view Shoppub</span>
        </span>
        <span className="flex items-center gap-2 text-[11px] text-smoke-dark">
          atualizado há {seg}s
          <span className="rounded-full bg-lime/15 px-2.5 py-0.5 font-bold text-lime">recebendo</span>
        </span>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2.5 lg:grid-cols-4">
        {/* visitantes agora */}
        <div className={`${card(0)} col-span-2 lg:col-span-1`} style={delay(0)}>
          <div className="text-[10px] font-bold uppercase tracking-wider text-smoke-dark">
            Visitantes agora
          </div>
          <div className="font-display text-4xl font-bold text-lime">{visitantes}</div>
          <div className="mt-0.5 text-[10px] text-smoke-dark">pico de hoje: 125 às 10:45</div>
          <div className="mt-2.5 flex h-1.5 overflow-hidden rounded-full bg-white/10">
            <span className="bg-lime" style={{ width: "64%" }} />
            <span className="bg-white/45" style={{ width: "34%" }} />
          </div>
          <div className="mt-1.5 flex gap-3 text-[10px] text-smoke-dark">
            <span><b className="text-paper">64%</b> celular</span>
            <span><b className="text-paper">34%</b> computador</span>
          </div>
        </div>

        {/* pageviews por minuto */}
        <div className={`${card(1)} col-span-2`} style={delay(1)}>
          <div className="flex items-baseline justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider text-smoke-dark">
              Pageviews por minuto — últimas 2h
            </span>
            <span className="text-[11px] font-bold text-paper">
              agora <span className="text-lime">{spark[spark.length - 1]}</span> pv/min
            </span>
          </div>
          <svg viewBox={`0 0 ${W} ${H}`} className="mt-2 w-full" preserveAspectRatio="none">
            <polygon points={`0,${H} ${pts} ${W},${H}`} fill="rgba(201,241,88,0.12)" />
            <polyline
              points={pts}
              fill="none"
              stroke="#c9f158"
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          </svg>
        </div>

        {/* fontes de tráfego */}
        <div className={card(2)} style={delay(2)}>
          <div className="text-[10px] font-bold uppercase tracking-wider text-smoke-dark">
            Fontes de tráfego
          </div>
          <div className="mt-1.5 flex items-center gap-3">
            <Donut />
            <div className="flex flex-col gap-1 text-[10px]">
              {FONTES.map((f) => (
                <span key={f.nome} className="flex items-center gap-1.5 text-smoke-dark">
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: f.cor }} />
                  {f.nome} <b className="text-paper">{f.pct}%</b>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* métricas do dia */}
        {[
          { label: "Visitantes únicos hoje", valor: "5.889", delta: "▲ 5%", up: true },
          { label: "Sessões hoje", valor: "6.514", delta: "▲ 7%", up: true },
          { label: "Pageviews — última hora", valor: "1.666", delta: "▼ 23%", up: false },
          { label: "Páginas por sessão", valor: "2,7", delta: "média do dia", up: null },
        ].map((m, i) => (
          <div key={m.label} className={card(3 + i)} style={delay(3 + i)}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-smoke-dark">
              {m.label}
            </div>
            <div className="mt-0.5 font-display text-2xl font-bold text-paper">{m.valor}</div>
            <div
              className={`text-[10px] font-semibold ${
                m.up === true ? "text-lime" : m.up === false ? "text-red-400/80" : "text-smoke-dark"
              }`}
            >
              {m.delta}
            </div>
          </div>
        ))}

        {/* funil */}
        <div className={`${card(7)} col-span-2 lg:col-span-3`} style={delay(7)}>
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider text-smoke-dark">
              Funil de compra — hoje
            </span>
            <span className="text-[10px] text-smoke-dark">
              carrinhos ativos agora: <b className="text-lime">{carrinhos}</b>
            </span>
          </div>
          <div className="mt-2.5 grid grid-cols-5 gap-1.5">
            {FUNIL.map((f, i) => (
              <div key={f.label}>
                <div className="font-display text-sm font-bold text-paper sm:text-lg">
                  {f.n.toLocaleString("pt-BR")}
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full rounded-full bg-lime transition-all duration-700"
                    style={{
                      width: on ? `${Math.max(f.pct, 6)}%` : "0%",
                      transitionDelay: `${1200 + i * 160}ms`,
                    }}
                  />
                </div>
                <div className="mt-1 truncate text-[9px] text-smoke-dark sm:text-[10px]">
                  {f.label} · {f.pct.toLocaleString("pt-BR")}%
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* estados agora */}
        <div className={`${card(8)} col-span-2 lg:col-span-1`} style={delay(8)}>
          <div className="text-[10px] font-bold uppercase tracking-wider text-smoke-dark">
            Estados — agora
          </div>
          <div className="mt-2 flex flex-col gap-1.5">
            {ESTADOS.map((e) => (
              <div key={e.uf}>
                <div className="flex justify-between text-[10px]">
                  <span className="text-smoke-dark">{e.uf}</span>
                  <b className="text-paper">{e.n}</b>
                </div>
                <div className="mt-0.5 h-1 overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full rounded-full bg-lime/80 transition-all duration-700"
                    style={{ width: on ? `${(e.n / 21) * 100}%` : "0%", transitionDelay: "1500ms" }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
